import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import jwtDecode from 'jwt-decode'
import getGeneralData from 'services/common/generalData'
import { IGeneralData } from 'types/common'
import { logout as authLogout, setAuth, setGeneralData } from 'ducks/auth'
import { logout as userLogout, setRuntimePermissions, setUser } from 'ducks/user/reducer'
import { userSelector } from 'ducks/user'
import { clearAuthenticatedSession, refreshAccessToken } from 'services/login/auth-session'

type RoleType = 'special_admin' | 'super_admin' | 'platform_admin' | 'super_platform_admin' | 'user'

interface IAccessTokenData {
  email?: string
  companyId?: number
  roleType?: RoleType
  userName?: string
  hasAdminPlatformManagement?: boolean
  hasQuestionnaireManagement?: boolean
}

const useAuthentication = (): boolean => {
  const dispatch = useDispatch()
  const currentToken: string = useSelector(userSelector).token
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const restoreSession = async (): Promise<void> => {
      const session = currentToken
        ? { token: currentToken }
        : await refreshAccessToken()

      if (!session) {
        clearAuthenticatedSession()
        dispatch(setGeneralData({}))
        dispatch(userLogout())
        dispatch(authLogout())
        setIsLoading(false)
        return
      }

      const tokenData: IAccessTokenData = jwtDecode(session.token)
      dispatch(
        setUser({
          token: session.token,
          email: tokenData.email || '',
          companyId: tokenData.companyId || 0,
          userName: tokenData.userName || '',
          hasAdminPlatformManagement: Boolean(tokenData.hasAdminPlatformManagement),
          hasQuestionnaireManagement: Boolean(tokenData.hasQuestionnaireManagement),
        })
      )
      dispatch(setAuth({ role: tokenData.roleType || '', isAuth: true }))

      try {
        const data = (await getGeneralData(
          session.token,
        )) as IGeneralData['data']
        if (data && typeof data === 'object') {
          dispatch(
            setGeneralData({
              data: Object.keys(data).length > 0 ? data : {},
            })
          )

          if (data.currentUserPermissions) {
            dispatch(
              setRuntimePermissions({
                hasAdminPlatformManagement: data.currentUserPermissions.hasAdminPlatformManagement,
                hasQuestionnaireManagement: data.currentUserPermissions.hasQuestionnaireManagement,
              })
            )
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    void restoreSession()
  }, [currentToken, dispatch])

  return isLoading
}

export default useAuthentication
